<template>
  <div class="dg reset-password-page">
    <header class="reset-password-header">
      <img
        alt="Rallyhood Logo"
        class="logo"
        src="/rallyhood-white.svg"
      >
    </header>

    <main class="reset-password-main-content ">
      <form
        class="reset-form"
        @submit.prevent="handleSubmit"
      >
        <h2 class="reset-form-title">
          Reset Your Password
        </h2>

        <div class="reset-form-group">
          <label
            for="reset-password"
            class="dg text-align-left"
          >Password</label>
          <div class="sh-password-input-container">
            <input
              id="reset-password"
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              name="password"
              autocomplete="new-password"
              class="sh-form-input sh-password-input"
              :class="{ 'input-error': errorMessages.password }"
              @click="scrollTop"
            >
            <button
              type="button"
              class="sh-toggle-password"
              :aria-label="showPassword ? 'Hide password' : 'Show password'"
              @click="togglePasswordVisibility('password')"
            >
              <i
                :class="showPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"
                aria-hidden="true"
              />
            </button>
          </div>
          <span
            v-show="errorMessages.password"
            class="sh-form-field-error"
          >{{ errorMessages.password }}</span>
        </div>

        <div class="reset-form-group">
          <label
            for="reset-password-confirm"
            class="dg text-align-left"
          >Confirm Password</label>
          <div class="sh-password-input-container">
            <input
              id="reset-password-confirm"
              v-model="passwordConfirmation"
              :type="showPasswordConfirmation ? 'text' : 'password'"
              name="passwordConfirmation"
              autocomplete="new-password"
              class="sh-form-input sh-password-input"
              :class="{ 'input-error': errorMessages.passwordConfirmation }"
              @click="scrollTop"
            >

            <button
              type="button"
              class="sh-toggle-password"
              :aria-label="showPasswordConfirmation ? 'Hide password' : 'Show password'"
              @click="togglePasswordVisibility('passwordConfirmation')"
            >
              <i
                :class="showPasswordConfirmation ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"
                aria-hidden="true"
              />
            </button>
          </div>

          <span
            v-show="errorMessages.passwordConfirmation"
            class="sh-form-field-error"
          >{{
            errorMessages.passwordConfirmation
          }}</span>
        </div>

        <button
          type="submit"
          class="sh-login-button reset-submit-button"
          :disabled="isSubmitting"
        >
          {{ isSubmitting ? 'Resetting...' : 'Reset Password' }}
        </button>
      </form>
    </main>

    <div class="sh-right-footer reset-form-footer">
      <a href="mailto:support@rallyhood.com">Contact Us</a>
      <span
        class="sh-footer-sep"
        aria-hidden="true"
      >&bull;</span>
      <a
        href="https://www.rallyhood.com/home/terms.php"
        target="_blank"
        rel="noopener"
      >Terms &amp; Conditions</a>
      <span
        class="sh-footer-sep"
        aria-hidden="true"
      >&bull;</span>
      <a
        href="https://www.rallyhood.com/home/privacy"
        target="_blank"
        rel="noopener"
      >Privacy Policy</a>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";

import { router } from "../common";
import { Capacitor } from "@capacitor/core";
import { userInfoPersistent } from "@/common/user-info";
import { App } from '@capacitor/app';
import { Preferences } from '@capacitor/preferences';
import { userInfo } from "@/common/user-info";

export default {
  emits: ["saveComplete"],
  data() {
    return {
      token: "",
      password: "",
      passwordConfirmation: "",
      showPassword: false,
      showPasswordConfirmation: false,
      isSubmitting: false,
      hasInteracted: { password: false, passwordConfirmation: false },
      errorMessages: { password: '', passwordConfirmation: '' },
    };
  },
  watch: {
    '$route.query.prt': {
      immediate: true,
      handler(val) {
        this.token = val || null
      }
    },
    password(val) {
      this.hasInteracted.password = true;
      this.validatePassword();
      if (this.hasInteracted.passwordConfirmation && this.passwordConfirmation) {
        this.validatePasswordConfirmation();
      }
    },
    passwordConfirmation(val) {
      this.hasInteracted.passwordConfirmation = true;
      this.validatePasswordConfirmation();
    }
  },
  created() {
    this.token = this.$route.query.prt || null;
    this.loadPageData();
  },
  methods: {
    ...mapActions("user", ["changeUserPassword", "resetPassword", "clearUsersState"]),
    validatePassword() {
      const pwd = this.password;
      if (!pwd) {
        this.errorMessages.password = 'Password is required.';
        return false;
      }
      if (pwd.length < 8 || !/[A-Z]/.test(pwd) || !/[0-9]/.test(pwd) || !/[^A-Za-z0-9]/.test(pwd)) {
        this.errorMessages.password = 'Your password must be at least 8 characters and contain one number, one special character, and one capital letter.';
        return false;
      }
      this.errorMessages.password = '';
      return true;
    },
    validatePasswordConfirmation() {
      if (!this.passwordConfirmation) {
        this.errorMessages.passwordConfirmation = 'Please confirm your password.';
        return false;
      }
      if (this.password !== this.passwordConfirmation) {
        this.errorMessages.passwordConfirmation = 'Passwords do not match.';
        return false;
      }
      this.errorMessages.passwordConfirmation = '';
      return true;
    },
    async handleSubmit() {
      this.hasInteracted.password = true;
      this.hasInteracted.passwordConfirmation = true;

      const isPasswordValid = this.validatePassword();
      const isConfirmValid = this.validatePasswordConfirmation();

      if (!isPasswordValid || !isConfirmValid) {
        return;
      }

      this.isSubmitting = true;

      try {
        if (this.token) {
          await this.resetPassword({ token: this.token, password: this.password });
          if (this.status?.error) {
            alert("Change password ERROR.")
            this.clearUsersState()
          } else {
            alert("Change password SUCCESS!")
            router.push("/login")
          }
        } else {
          this.changeUserPassword({ updatedUser: this.currentUser, password: this.password })
          this.$emit('saveComplete');
          this.$store.dispatch('user/flashSuccess', 'Password successfully updated!');
        }
      } catch (e) {
        console.error('Reset password error:', e);
        alert("An error occurred. Please try again.");
      } finally {
        this.isSubmitting = false;
      }
    },
    async changePassword() {

      if (this.passwordError || this.passwordConfirmationError) {
        this.error = 'Passwords must match and be at least 8 characters long, contain a number, a lowercase letter, an uppercase letter, and a symbol'
        return
      }
      this.error = ''
      if (this.resetToken) {
        await this.resetPassword({ token: this.resetToken, password: this.password });
        if (this.status.error) {
          alert("Change password ERROR.")
          this.clearUsersState()
        } else {
          alert("Change password SUCCESS!")
          router.push("/login")
        }
      } else {
        this.changeUserPassword({ updatedUser: this.currentUser, password: this.password })
        this.$emit('saveComplete');
        this.$store.dispatch('user/flashSuccess', 'Password successfully updated!');
      }

    },
    async loadPageData() {
      this.user_id = userInfo()?.user_id;
    },
    togglePasswordVisibility(field) {
      if (field === 'password') {
        this.showPassword = !this.showPassword
      } else if (field === 'passwordConfirmation') {
        this.showPasswordConfirmation = !this.showPasswordConfirmation
      }
    },
    cancel() {
      router.push("/login");
    },
    scrollBack() {
      this.$refs["loginform"].style = ''
    },
    scrollTop() {
      console.log(this.screenWidth)
      if (this.screenWidth < 960) {
        this.$refs["loginform"].style = 'position: fixed; top: 25px; width: 100%'
      }
    },
    scrollToTop() {
      this.$el.scrollTop = 0
    }
  }
};
</script>
